import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tag } from 'antd';
import resetIcon from '../../../../../../img/reset.svg';

/**
 * Strips HTML tags from a string
 * @param {string} html - HTML string
 * @returns {string} Plain text without HTML tags
 */
const stripHtml = (html) => {
  if (!html || typeof html !== 'string') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Filter label mappings for translation
 * @type {Object<string, string>}
 */
const FILTER_LABELS = {
  product_names: 'product|filters.Product name',
  active_substance_names: 'product|filters.Active substance name',
  organic_approval: 'product|filters.Organic approval',
  cultures: 'product|filters.Culture (from treatments)',
  cultures_practices: 'product|filters.Cultural practices',
  activity_sectors: 'product|filters.Activity sector',
  enemies: 'product|filters.Enemies',
  product_types: 'product|filters.Product types',
  activity_types: 'product|filters.Activity types',
  allergens: 'product|filters.Allergens',
  chemical_families: 'product|filters.Chemical families or biological families',
  pesticide_groups: 'product|filters.Pesticide groups',
  arla_culture_groups: 'product|filters.ARLA Culture Groups',
  application_types: 'product|filters.Application Types',
  names: 'product|filters.Active substance name',
  cas_numbers: 'product|filters.CAS number',
  pesticide_categories: 'product|filters.Pesticide categories',
  pesticide_types: 'product|filters.Pesticide types',
  // Health risk filters
  acute_toxicity: 'product|filters.Acute toxicity',
  long_term_effects: 'product|filters.Long term effects',
  // Environment risk filters
  fish_or_daphnia: 'product|filters.Fish or daphnia',
  birds: 'product|filters.Birds',
  bees: 'product|filters.Bees',
  persistence: 'product|filters.Persistence',
  leaching_potential: 'product|filters.Leaching potential',
};

/**
 * Transforms filters object into active filter display format
 * @param {Object} filters - Current filter state
 * @param {Function} getFilterLabel - Function to get display labels
 * @param {Function} t - Translation function
 * @returns {Array<Object>} Array of active filter objects
 */
const getActiveFilters = (filters, getFilterLabel, t) => {
  return Object.entries(filters || {})
    .filter(([key, value]) => {
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        key !== 'page' &&
        key !== 'size'
      );
    })
    .map(([key, value]) => ({
      key,
      label: FILTER_LABELS[key] ? t(FILTER_LABELS[key]) : key,
      values: value.map((v) => ({
        id: v,
        label: getFilterLabel(key, v),
      })).filter((item) => item.label),
    }))
    .filter((filter) => filter.values.length > 0);
};



/**
 * Active filters display section
 * @param {Object} props - Component props
 * @param {Array} props.activeFilters - List of active filters
 * @param {Function} props.onResetFilters - Reset filters callback
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} Active filters section
 */
const ActiveFiltersSection = ({ activeFilters, onResetFilters, onRemoveFilter, t, hasFilters }) => {
  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  const validFilters = activeFilters.map((filter) => ({
    ...filter,
    values: filter.values.filter((value) => value.label && !isUUID(value.label)),
  })).filter((filter) => filter.values.length > 0);

  const filterCount = validFilters.reduce((sum, filter) => sum + filter.values.length, 0);

  return (
    <div className="bg-green-900 text-lg p-6 rounded-2xl mb-8">
      <div className="flex justify-between items-center">
        <h3 className="text-white text-lg font-medium m-0 normal-case">
          {t('common|Active filters')} {filterCount > 0 && `(${filterCount})`}
        </h3>
        <div className="flex gap-2">
          <Button size="large" onClick={onResetFilters} className="reset-button-with-icon">
            <img src={resetIcon} alt="Reset" className="w-4 h-4 reset-icon" />
            {t('common|Reset')}
          </Button>
        </div>
      </div>
      {validFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {validFilters.map((filter) =>
            filter.values.map((value) => (
              <Tag
                key={`${filter.key}-${value.id}`}
                closable
                onClose={() => onRemoveFilter(filter.key, value.id)}
                title={stripHtml(value.label)}
              >
                <strong>{filter.label}:</strong> {stripHtml(value.label)}
              </Tag>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main recap component that displays active filters with options to reset or share results
 * Works with nuqs for automatic URL synchronization
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter state
 * @param {Function} props.onResetFilters - Callback to reset all filters
 * @param {Function} props.getFilterLabel - Function to get display label for filter values
 * @returns {JSX.Element} Recap component with filters and legend
 */
const Recap = ({ filters, onResetFilters, onRemoveFilter, getFilterLabel }) => {
  const { t } = useTranslation();

  const activeFilters = getActiveFilters(filters, getFilterLabel, t);

  const hasAnyFilters = Object.entries(filters || {}).some(
    ([key, value]) =>
      Array.isArray(value) &&
      value.length > 0 &&
      key !== 'page' &&
      key !== 'size'
  );


  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  const validFilters = activeFilters.map((filter) => ({
    ...filter,
    values: filter.values.filter((value) => value.label && !isUUID(value.label)),
  })).filter((filter) => filter.values.length > 0);

  const hasValidLabels = validFilters.length > 0;

  return (
    <>
      {hasValidLabels && (
        <ActiveFiltersSection
          activeFilters={activeFilters}
          onResetFilters={onResetFilters}
          onRemoveFilter={onRemoveFilter}
          hasFilters={hasAnyFilters}
          t={t}
        />
      )}
    </>
  );
};

export default Recap;
